import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getInterestMeta } from "../../features/interests";
import { getEventThumbnailUrl } from "../../features/events/media/eventImageUrl";
import { haversineDistanceKm } from "../../utils/geoDistance";
import UserAvatar from "../userAvatar/UserAvatar";
import styles from "./EventListItem.module.css";

function formatDistanceKm(distanceKm) {
  if (!Number.isFinite(distanceKm) || distanceKm < 0) return "";
  if (distanceKm < 1) return "< 1 km";
  return `${Math.round(distanceKm)} km`;
}

// Liste (contexte "discover") : le bouton coeur partage la même barre que
// les avatars/le compteur — reste prudent pour ne jamais le pousser hors du
// cadre sur un petit écran.
const AVATAR_VISIBLE_MAX_LIST = 6;
// Agenda : pas de bouton coeur dans cette barre (cf. shouldShowInterestAction),
// la place libérée permet d'afficher plus d'avatars sans risque de débordement.
const AVATAR_VISIBLE_MAX_AGENDA = 8;

function FavoriteGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path
        d="M12 20.25l-1.09-.99C5.55 14.39 2 11.17 2 7.25 2 4.26 4.42 2 7.3 2c1.64 0 3.2.77 4.2 2.01A5.42 5.42 0 0115.7 2C18.58 2 21 4.26 21 7.25c0 3.92-3.55 7.14-8.91 12.01L12 20.25z"
        fill="currentColor"
      />
    </svg>
  );
}

function SecretGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path
        d="M7.25 10.25V8a4.75 4.75 0 019.5 0v2.25h.5a2 2 0 012 2v5.5a2 2 0 01-2 2H6.75a2 2 0 01-2-2v-5.5a2 2 0 012-2h.5zm1.5 0h6.5V8a3.25 3.25 0 00-6.5 0v2.25zM12 14a1 1 0 00-.5 1.87v1.38a.5.5 0 001 0v-1.38A1 1 0 0012 14z"
        fill="currentColor"
      />
    </svg>
  );
}

function EventListItem({
  evt,
  distanceOriginCenter = null,
  isSelected = false,
  isInterested = false,
  isInterestPending = false,
  context = "discover",
  contextBadges = [],
  showInterestAction,
  onClick,
  onToggleInterest,
  readOnly = false,
  disableHoverMotion = false,
}) {
  const isAgendaContext = context === "agenda";
  const shouldShowInterestAction = readOnly
    ? false
    : typeof showInterestAction === "boolean"
      ? showInterestAction
      : !isAgendaContext;
  const shouldShowActionBar = shouldShowInterestAction;
  const normalizedContextBadges = Array.isArray(contextBadges)
    ? contextBadges
        .map((badge) => {
          if (badge && typeof badge === "object") {
            const label = String(badge.label || "").trim();
            return label ? { label, tone: String(badge.tone || "").trim() } : null;
          }

          const label = String(badge || "").trim();
          return label ? { label, tone: "" } : null;
        })
        .filter(Boolean)
        .slice(0, 2)
    : [];
  const {
    categoryMeta,
    previewImageUrl,
    cityLabel,
    distanceLabel,
    dateLabel,
    dateBadgeStyle,
    capacity,
    attendeesCount,
  } = useMemo(() => {
    const _capacity = evt.maxParticipants || evt.capacity || 0;
    const _attendees = Math.max(0, Number(evt.attendeesCount) || 0);
    const _tone = resolveCategoryTone(evt.categoryLabel || evt.category);
    // La distance n'a de sens que quand la recherche part d'un point precis
    // (Autour de moi / zone carte) — cf. discussion sur "Autour de moi"
    // montrant des sorties lointaines sans que ce soit visible nulle part.
    const _distanceKm = distanceOriginCenter
      ? haversineDistanceKm(evt.location, distanceOriginCenter)
      : null;
    return {
      categoryMeta: getInterestMeta(evt.category),
      previewImageUrl: getEventThumbnailUrl(evt),
      cityLabel: evt.location?.city || "Lieu à définir",
      distanceLabel: formatDistanceKm(_distanceKm),
      dateLabel: formatEventCardDate(evt.date),
      dateBadgeStyle: {
        "--date-badge-bg-start": _tone.bgStart,
        "--date-badge-bg-end": _tone.bgEnd,
        "--date-badge-accent": _tone.accent,
        "--date-badge-border": _tone.border,
        "--date-badge-text": _tone.text,
        "--date-badge-category-text": _tone.categoryText,
        "--date-badge-shadow": _tone.shadow,
        "--date-badge-glow": _tone.glow,
      },
      capacity: _capacity,
      attendeesCount: _attendees,
    };
  }, [evt, distanceOriginCenter]);

  const socialPreviewProfiles = useMemo(
    () => resolveAttendeePreviewProfiles(evt),
    [evt]
  );
  // La jauge est une seule et meme barre pour les avatars, le compteur et
  // (en liste) le bouton coeur : ce dernier consomme de la place que
  // l'Agenda n'a pas a reserver (coeur masque, cf. shouldShowInterestAction).
  // Plafond plus genereux en Agenda, plus prudent en liste pour ne jamais
  // pousser le compteur/le coeur hors du cadre — cf. AVATAR_VISIBLE_MAX_*.
  const avatarVisibleMax = isAgendaContext
    ? AVATAR_VISIBLE_MAX_AGENDA
    : AVATAR_VISIBLE_MAX_LIST;
  const shownSocialProfiles = useMemo(
    () => socialPreviewProfiles.slice(0, avatarVisibleMax),
    [socialPreviewProfiles, avatarVisibleMax]
  );
  // Base sur le vrai total (attendeesCount), pas sur la taille du tableau
  // d'apercu recu : ce dernier peut etre plus petit que le nombre reel
  // d'inscrits (plafond cote backend), le "+N" resterait alors faux ou
  // n'apparaitrait jamais meme quand la sortie est bien plus peuplee que
  // les avatars affiches.
  const socialProfilesOverflowCount = Math.max(
    0,
    attendeesCount - shownSocialProfiles.length
  );

  const gaugeStyle = useMemo(() => {
    const fill = resolveSocialGaugeFillPercent({ capacity, attendeesCount }) ?? 0;
    return { "--fill": `${fill}%` };
  }, [capacity, attendeesCount]);

  const normalizedCapacity = Math.max(0, Number(capacity) || 0);
  const agendaGaugeLabel =
    normalizedCapacity > 0 ? `${attendeesCount}/${normalizedCapacity}` : `${attendeesCount}`;
  const attendanceLabel = agendaGaugeLabel;
  const attendanceAriaLabel = normalizedCapacity > 0
    ? `${attendeesCount} inscrits sur ${normalizedCapacity} places`
    : `${attendeesCount} inscrits`;
  const [isHeartBursting, setIsHeartBursting] = useState(false);
  const heartBurstTimeoutRef = useRef(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [previewImageUrl]);

  useEffect(() => {
    return () => {
      if (heartBurstTimeoutRef.current) {
        clearTimeout(heartBurstTimeoutRef.current);
      }
    };
  }, []);

  const handleItemClick = useCallback(
    (event) => {
      onClick?.(evt, event);
    },
    [evt, onClick]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleItemClick(event);
      }
    },
    [handleItemClick]
  );

  const handleInterestClick = useCallback(
    (event) => {
      event.stopPropagation();
      // Garde-fou en plus du masquage du bouton (shouldShowInterestAction) :
      // meme si ce handler etait invoque autrement, un lien public en lecture
      // seule ne doit jamais pouvoir declencher de mutation.
      if (readOnly) return;
      if (isInterestPending) return;
      setIsHeartBursting(false);
      if (heartBurstTimeoutRef.current) {
        clearTimeout(heartBurstTimeoutRef.current);
      }
      setIsHeartBursting(true);
      heartBurstTimeoutRef.current = setTimeout(() => {
        setIsHeartBursting(false);
      }, 520);
      onToggleInterest?.(evt.id, "INTERESTED");
    },
    [evt.id, isInterestPending, onToggleInterest, readOnly]
  );

  return (
    <article
      className={[
        styles.item,
        disableHoverMotion && styles.itemStatic,
        isAgendaContext && styles.itemAgenda,
        isSelected && styles.selected,
        `category-${evt.category || "default"}`,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleItemClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-selected={isSelected}
    >
      <div className={styles.topRightDock}>
        {distanceLabel ? (
          <div className={styles.distanceBadge}>
            <span className={styles.distanceDot} />
            {distanceLabel}
          </div>
        ) : null}
        <div className={styles.city}>
          <span className={styles.cityDot} />
          {cityLabel}
        </div>
      </div>

      {dateLabel ? (
        <div className={styles.mediaDateDock}>
          <span className={styles.mediaDateBadge}>{dateLabel}</span>
        </div>
      ) : null}

      <div className={styles.media}>
        {previewImageUrl && !imgError ? (
          <img
            src={previewImageUrl}
            alt={evt.title}
            loading="lazy"
            decoding="async"
            // React 18.3.1 (installe dans ce projet) ne reconnait pas la prop
            // camelCase `fetchPriority` (warning DOM confirme par test
            // navigateur reel) — attribut HTML fetchpriority conserve.
            fetchpriority="low"
            width="132"
            height="132"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={styles.mediaFallback} aria-hidden="true">
            <span>{categoryMeta.emoji}</span>
          </div>
        )}
        {evt.startTime ? <span className={styles.mediaTimeCorner}>{evt.startTime}</span> : null}
      </div>

      <div className={styles.dateDock} style={dateBadgeStyle}>
        <span className={styles.dateDockCategory}>
          {categoryMeta.emoji} {categoryMeta.label}
        </span>
      </div>

      <div className={styles.body}>
        <div className={styles.bodyTop}>
          <div className={styles.titleShell}>
            <h3 className={styles.title}>{evt.title}</h3>
            {evt.organizer?.isApprovedOrganizer === true ? (
              <img
                className={styles.certifiedMark}
                src="/organisateur-certifie.svg"
                alt="Organisateur certifié"
                title="Organisateur certifié"
                width="26"
                height="26"
                loading="lazy"
                decoding="async"
              />
            ) : null}
          </div>
          {normalizedContextBadges.length > 0 ? (
            <div className={styles.contextBadgeRow} aria-label="Informations sortie">
              {normalizedContextBadges.map((badge, index) => {
                const isSecretBadge = badge.tone === "secret";
                return (
                  <span
                    key={`${badge.label}-${index}`}
                    className={`${styles.contextBadge} ${
                      isSecretBadge ? styles.contextBadgeSecret : ""
                    }`.trim()}
                  >
                    {isSecretBadge ? (
                      <SecretGlyph className={styles.contextBadgeIcon} />
                    ) : null}
                    {badge.label}
                  </span>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className={styles.mobilitySlot} aria-hidden="true" />

        <div className={styles.bodyBottom}>
          <div
            className={styles.attendanceBar}
            style={gaugeStyle}
            aria-label={attendanceAriaLabel}
          >
            {shownSocialProfiles.length > 0 ? (
              <div className={styles.socialAvatarScroll} aria-hidden="true">
                <div className={styles.socialAvatars}>
                  {shownSocialProfiles.map((profile, index) => (
                    <UserAvatar
                      key={profile.id || profile.uid || `${evt.id}-social-${index}`}
                      user={profile}
                      name={profile.displayName}
                      size={30}
                      className={styles.socialAvatar}
                      ringClassName={styles.socialAvatarRing}
                      imageClassName={styles.socialAvatarImage}
                      fallbackClassName={styles.socialAvatarFallback}
                    />
                  ))}
                  {socialProfilesOverflowCount > 0 ? (
                    <span className={styles.socialAvatarMore}>
                      +{socialProfilesOverflowCount}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
            <span className={styles.attendanceSocialProof}>{attendanceLabel}</span>
            {shouldShowActionBar ? (
              <div className={styles.attendanceActions}>
                {shouldShowInterestAction ? (
                  <button
                    type="button"
                    className={`${styles.action} ${styles.actionHeart} ${
                      isInterested ? styles.actionInterested : ""
                    } ${isHeartBursting ? styles.actionHeartBurst : ""}`.trim()}
                    aria-label={isInterested ? "Retirer l'intérêt" : "Je suis intéressé"}
                    aria-pressed={isInterested}
                    aria-disabled={isInterestPending}
                    disabled={isInterestPending}
                    onClick={handleInterestClick}
                  >
                    <FavoriteGlyph className={styles.actionIcon} />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

export default memo(EventListItem);

function resolveAttendeePreviewProfiles(eventItem = {}) {
  const attendees = Array.isArray(eventItem?.attendeePreviewProfiles)
    ? eventItem.attendeePreviewProfiles
    : Array.isArray(eventItem?.socialPreviewProfiles)
    ? eventItem.socialPreviewProfiles
    : [];
  const seen = new Set();
  const merged = [];
  for (const profile of attendees) {
    const id = profile.id || profile.uid;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    merged.push(profile);
    if (merged.length >= 20) break;
  }
  return merged;
}

function resolveSocialGaugeFillPercent({ capacity, attendeesCount }) {
  const normalizedCapacity = Number(capacity);

  if (!Number.isFinite(normalizedCapacity) || normalizedCapacity <= 0) {
    return null;
  }

  return Math.max(
    8,
    Math.min(
      100,
      Math.round((Math.max(0, Number(attendeesCount) || 0) / normalizedCapacity) * 100)
    )
  );
}


// Format court (pas "Samedi 12 Septembre") : ce libelle est colle a l'heure
// dans le badge en coin de l'image (cf. topRightDock qui a besoin de la
// place liberee par l'ancien dateDock pleine largeur pour la distance
// d'eloignement, sans se superposer).
function formatEventCardDate(dateValue) {
  if (!dateValue) {
    return "Date à définir";
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return "Date à définir";
  }

  const formatted = parsedDate.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  return toTitleCaseWords(formatted);
}

function toTitleCaseWords(value) {
  return String(value || "").replace(/\b([a-zÃ Ã¢Ã¤Ã©Ã¨ÃªÃ«Ã¯Ã®Ã´Ã¶Ã¹Ã»Ã¼Ã§])/g, (letter) =>
    letter.toUpperCase()
  );
}

const CATEGORY_HUES = {
  sorties:  30,
  food:     12,
  musique:  280,
  danse:    330,
  sport:    205,
  bienetre: 168,
  nature:   128,
  voyage:   192,
  culture:  258,
  gaming:   248,
  famille:  44,
  pro:      218,
};

function resolveCategoryTone(category) {
  const normalized = String(category || "").toLowerCase().trim();
  const hue = CATEGORY_HUES[normalized] ?? 218;
  const accentHue = (hue + 18) % 360;

  return {
    bgStart: `hsla(${hue}, 46%, 17%, 0.92)`,
    bgEnd: `hsla(${hue}, 42%, 12%, 0.94)`,
    accent: `hsla(${accentHue}, 74%, 64%, 0.18)`,
    border: `hsla(${accentHue}, 72%, 78%, 0.26)`,
    text: "rgba(241, 245, 249, 0.95)",
    categoryText: `hsla(${accentHue}, 72%, 86%, 0.86)`,
    shadow: `hsla(${hue}, 60%, 18%, 0.26)`,
    // Meme famille visuelle que la distance (vert fluo) et "Changer" (violet
    // fluo), une teinte par categorie plutot qu'une couleur fixe. Alpha
    // modere (0.5) car cette valeur est reutilisee telle quelle sur 4 usages
    // d'intensite differente (bordure, glow externe, glow interne, texte) —
    // une valeur trop haute (ex. 0.9) sature tout au meme niveau et donne un
    // effet "autocollant" plat plutot qu'un liseré lumineux discret.
    glow: `hsla(${accentHue}, 76%, 62%, 0.5)`,
  };
}
